import { getChatCompletion } from '../aiIntegrations/chatCompletion';
import { supabase } from '../../lib/supabase';

const TRIAGE_SYSTEM_PROMPT = `You are a fire forensics expert conducting preliminary triage analysis following NFPA 921 and ASTM E2713 standards.

Your role is to:
1. Analyze uploaded images and documents for fire origin, cause, and progression indicators
2. Identify what information is present and what is missing
3. Generate clarifying questions to fill knowledge gaps
4. Assess confidence level in making a preliminary determination

CRITICAL ANTI-HALLUCINATION RULES:
- Only describe what is DIRECTLY OBSERVABLE in the images
- Never infer or assume information not visible
- Use phrases like "appears to show", "visible evidence suggests", "observable patterns indicate" - If something is not visible, explicitly state"not visible in provided images"
- Do not fabricate witness statements, timestamps, or measurements not provided

Output Format (JSON):
{
  "summary": "Brief triage summary of observable findings",
  "confidence": 0.75,
  "observableFindings": ["finding1", "finding2"],
  "missingInformation": ["info1", "info2"],
  "clarifyingQuestions": [
    {"question": "text", "priority": "required|optional", "rationale": "why needed", "needs_image": false}
  ]
}

For each clarifying question, set "needs_image" to true ONLY when the question explicitly asks the user to provide additional photographs, images, video footage, or visual evidence of the scene. Set it to false for all text-answer questions.`;

export async function runTriageAnalysis(caseData, imageDescriptions) {
  const userPrompt = `
Case Information:
- Incident Date: ${caseData?.incidentDate || caseData?.incident_date}
- Property Type: ${caseData?.propertyType || caseData?.property_type}
- Allegation: ${caseData?.allegationType || caseData?.allegation_type}
- Location: ${caseData?.propertyAddress || caseData?.property_address}
- Additional Context: ${caseData?.additionalContext || caseData?.additional_context || 'None provided'}
${caseData?.previousQA ? `\nPrevious Clarification Rounds:\n${caseData.previousQA}\n` : ''}
Image Analysis:
${imageDescriptions?.map((desc, idx) => `Image ${idx + 1}: ${desc}`)?.join('\n') || 'No images provided.'}
${caseData?.ocrContext || ''}

Provide triage analysis in JSON format.${caseData?.previousQA ? ' Given the answers already provided, only ask NEW clarifying questions that have not been addressed yet.' : ''}

IMPORTANT — Set your confidence score honestly:
- 0.3-0.5 = critical information is missing, clarifying questions are needed
- 0.5-0.7 = some useful details still missing, another round would meaningfully improve the report
- 0.7-0.9 = evidence is sufficient for a competent preliminary report
- 0.9-1.0 = comprehensive evidence, very detailed report possible

If the information provided is enough to write a useful report, set confidence >= 0.7 and return an EMPTY clarifyingQuestions array. Only include questions whose answers would MATERIALLY change or strengthen the report conclusions. Do not ask questions just for completeness.`;

  try {
    // Fetch Phase 1 model from system settings
    const { data: settingData, error: settingError } = await supabase?.from('system_settings')?.select('setting_value')?.eq('setting_key', 'ai_model_phase1')?.single();

    if (settingError) {
      console.error('Error fetching Phase 1 model setting:', settingError);
    }

    const modelToUse = settingData?.setting_value || 'gpt-4o';

    const response = await getChatCompletion(
      'OPEN_AI',
      modelToUse,
      [
        { role: 'system', content: TRIAGE_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt }
      ],
      {
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'TriageAnalysis',
            schema: {
              type: 'object',
              properties: {
                summary: { type: 'string' },
                confidence: { type: 'number' },
                observableFindings: {
                  type: 'array',
                  items: { type: 'string' }
                },
                missingInformation: {
                  type: 'array',
                  items: { type: 'string' }
                },
                clarifyingQuestions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      question: { type: 'string' },
                      priority: { type: 'string' },
                      rationale: { type: 'string' },
                      needs_image: { type: 'boolean' }
                    },
                    required: ['question', 'priority', 'rationale', 'needs_image'],
                    additionalProperties: false
                  }
                }
              },
              required: ['summary', 'confidence', 'observableFindings', 'missingInformation', 'clarifyingQuestions'],
              additionalProperties: false
            },
            strict: true
          }
        },
        max_completion_tokens: 2000
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    const triageData = JSON.parse(content);

    return {
      success: true,
      data: triageData,
      usage: response?.usage
    };
  } catch (error) {
    console.error('Triage analysis error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to complete triage analysis'
    };
  }
}

export async function analyzeImageWithVision(imageBase64, caseContext) {
  const systemPrompt = `You are a fire forensics expert analyzing scene photographs. Describe ONLY what is directly visible in the image. Focus on:
- Burn patterns and char depth
- Structural damage and material conditions
- Visible ignition sources or electrical components
- Smoke patterns and heat indicators
- Any visible evidence markers or scale references

Do NOT infer, assume, or fabricate details not visible in the image.`;

  const userPrompt = `Analyze this fire scene image. Context: ${caseContext?.allegationType} at ${caseContext?.propertyType}. Describe observable evidence only.`;

  try {
    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'user',
          content: [
            { type: 'text', text: userPrompt },
            {
              type: 'image_url',
              image_url: {
                url: imageBase64,
                detail: 'high'
              }
            }
          ]
        }
      ],
      {
        max_completion_tokens: 500
      }
    );

    const description = response?.choices?.[0]?.message?.content;

    return {
      success: true,
      description,
      usage: response?.usage
    };
  } catch (error) {
    console.error('Vision analysis error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to analyze image'
    };
  }
}

export async function extractHandwrittenText(imageBase64, fileName) {
  const systemPrompt = `You are an OCR specialist extracting handwritten text from images. Extract ALL visible handwritten text with high accuracy.

Rules:
- Extract text exactly as written, preserving spelling and formatting
- Indicate confidence level for each text region (0.0 to 1.0)
- Detect language of handwritten text
- Assess overall quality: "good" (clear, legible), "fair" (some unclear areas), "poor" (difficult to read)
- If no handwritten text is visible, return empty text with explanation
- For multiple text regions, separate them clearly`;

  const userPrompt = `Extract all handwritten text from this image. Provide:
1. Full extracted text
2. Overall confidence score (0.0-1.0)
3. Detected language
4. Quality assessment
5. Individual text regions with confidence scores if multiple areas exist`;

  try {
    const startTime = Date.now();

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        { role: 'system', content: systemPrompt },
        {
          role: 'user',
          content: [
            { type: 'text', text: userPrompt },
            {
              type: 'image_url',
              image_url: {
                url: imageBase64,
                detail: 'high'
              }
            }
          ]
        }
      ],
      {
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'HandwrittenTextExtraction',
            schema: {
              type: 'object',
              properties: {
                text: { type: 'string' },
                confidence: { type: 'number' },
                language: { type: 'string' },
                quality: { type: 'string' },
                regions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      text: { type: 'string' },
                      confidence: { type: 'number' },
                      bounding_box: {
                        type: 'object',
                        properties: {
                          x: { type: 'number' },
                          y: { type: 'number' },
                          width: { type: 'number' },
                          height: { type: 'number' }
                        },
                        required: ['x', 'y', 'width', 'height'],
                        additionalProperties: false
                      }
                    },
                    required: ['text', 'confidence'],
                    additionalProperties: false
                  }
                }
              },
              required: ['text', 'confidence', 'language', 'quality', 'regions'],
              additionalProperties: false
            },
            strict: true
          }
        },
        max_completion_tokens: 1500
      }
    );

    const processingTime = Date.now() - startTime;
    const content = response?.choices?.[0]?.message?.content;
    const ocrData = JSON.parse(content);

    return {
      success: true,
      data: {
        ...ocrData,
        processing_time_ms: processingTime,
        extracted_at: new Date()?.toISOString(),
        model_used: 'gpt-4o',
        file_name: fileName
      },
      usage: response?.usage
    };
  } catch (error) {
    console.error('Handwritten text extraction error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to extract handwritten text'
    };
  }
}